import React from "react";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";
import { useOutletContext } from "react-router-dom";
import { books_getSales, books_getInventory, books_getBundle } from "../../lib/firestoreAdapters";
import { collection, getDocs, addDoc, updateDoc, doc, query, where } from "firebase/firestore";
import { db } from "../../services/firebase";
import { refreshStudentsFromFirestore } from "../../services/studentsService";

function keySales(campus, sy) { return `books_sales_${String(campus).toLowerCase()}_${sy}`; }
function loadSales(campus, sy) { try { const raw = localStorage.getItem(keySales(campus, sy)); return raw ? JSON.parse(raw) : []; } catch { return []; } }
function keyInv(campus, sy) { return `books_inventory_${String(campus).toLowerCase()}_${sy}`; }
function loadInv(campus, sy) { try { const raw = localStorage.getItem(keyInv(campus, sy)); return raw ? JSON.parse(raw) : []; } catch { return []; } }

export default function BooksReports() {
  const { campus, schoolYear, setCampus, setSchoolYear, years } = useOutletContext();
  const [date, setDate] = React.useState(() => new Date().toISOString().slice(0,10));
  const [month, setMonth] = React.useState(() => new Date().toISOString().slice(0,7));
  const [sales, setSales] = React.useState([]);
  const [inv, setInv] = React.useState([]);
  const [adj, setAdj] = React.useState({}); // key: publisher -> {freight, discount}
  const [gradeFilter, setGradeFilter] = React.useState("");
  const [sectionFilter, setSectionFilter] = React.useState("");
  const [studentItems, setStudentItems] = React.useState([]);
  const [students, setStudents] = React.useState([]);
  const [bundle, setBundle] = React.useState(null);
  React.useEffect(() => {
    async function fetch() {
      if (USE_LOCAL_FALLBACK) {
        setSales(loadSales(campus, schoolYear));
        setInv(loadInv(campus, schoolYear));
      } else {
        const s = await books_getSales(campus, schoolYear);
        const i = await books_getInventory(campus, schoolYear);
        setSales(s); setInv(i);
        try {
          const snap = await getDocs(collection(db, "books_publisher_adjustments"));
          const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))
            .filter(r => r.campus === campus && r.schoolYear === schoolYear);
          const map = {};
          for (const r of rows) map[r.publisher] = { freight: Number(r.freight || 0), discount: Number(r.discount || 0), id: r.id };
          setAdj(map);
        } catch (e) { setAdj({}); }
        try {
          const snap = await getDocs(query(collection(db, "books_student_items"),
            where("campus","==",campus),
            where("schoolYear","==",schoolYear)
          ));
          setStudentItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch { setStudentItems([]); }
        try {
          const s = await refreshStudentsFromFirestore({ campus });
          setStudents(s || []);
        } catch { setStudents([]); }
      }
    }
    fetch();
  }, [campus, schoolYear]);

  React.useEffect(() => {
    (async () => {
      if (!gradeFilter) { setBundle(null); return; }
      try {
        const b = await books_getBundle(campus, schoolYear, gradeFilter);
        setBundle(b || null);
      } catch { setBundle(null); }
    })();
  }, [campus, schoolYear, gradeFilter]);

  const daily = sales.filter(s => s.date === date);
  const dailyTotal = daily.reduce((sum, s) => sum + Number(s.total || 0), 0);
  const monthly = sales.filter(s => String(s.date).startsWith(month));
  const monthlyTotal = monthly.reduce((sum, s) => sum + Number(s.total || 0), 0);

  // Publisher settlement: sum costs for sold units
  const soldByBook = {};
  for (const s of sales) for (const it of s.items) soldByBook[it.id] = (soldByBook[it.id] || 0) + Number(s.qty || 0);
  const settlement = inv
    .filter(b => soldByBook[b.id])
    .map(b => ({
      publisher: b.publisher || "-",
      title: b.title,
      sold: soldByBook[b.id],
      payable: Number(b.cost || 0) * Number(soldByBook[b.id] || 0)
    }));
  const settlementByPublisher = Object.values(settlement.reduce((acc, r) => {
    const k = r.publisher;
    if (!acc[k]) acc[k] = { publisher: k, sold: 0, payable: 0 };
    acc[k].sold += r.sold; acc[k].payable += r.payable;
    return acc;
  }, {}));

  async function saveAdj(p) {
    const a = adj[p] || { freight: 0, discount: 0 };
    if (USE_LOCAL_FALLBACK) {
      alert("Adjustments are cloud-only in dev mode.");
      return;
    }
    if (a.id) {
      await updateDoc(doc(db, "books_publisher_adjustments", a.id), { campus, schoolYear, publisher: p, freight: Number(a.freight || 0), discount: Number(a.discount || 0), updatedAt: new Date().toISOString() });
    } else {
      const res = await addDoc(collection(db, "books_publisher_adjustments"), { campus, schoolYear, publisher: p, freight: Number(a.freight || 0), discount: Number(a.discount || 0), createdAt: new Date().toISOString() });
      setAdj(prev => ({ ...prev, [p]: { ...a, id: res.id } }));
    }
    alert("Saved.");
  }

  const gradesAvailable = Array.from(new Set(inv.map(b => String(b.grade || "")))).filter(Boolean).sort();
  const studentsInScope = students.filter(s => {
    const sameCampus = String(s.campus || "").toLowerCase() === String(campus).toLowerCase();
    const gradeOk = !gradeFilter || String(s.gradeLevel || s.grade || "").toLowerCase() === String(gradeFilter).toLowerCase();
    const sectionOk = !sectionFilter || String(s.section || (Array.isArray(s.sections) ? s.sections[0] : "") || "").toLowerCase() === String(sectionFilter).toLowerCase();
    return sameCampus && gradeOk && sectionOk;
  });
  const ownedByStudent = new Map();
  for (const it of studentItems) {
    if (it.campus !== campus || it.schoolYear !== schoolYear) continue;
    if (gradeFilter && String(it.grade || "").toLowerCase() !== String(gradeFilter).toLowerCase()) continue;
    const key = it.studentId || it.studentName;
    if (!key) continue;
    const set = ownedByStudent.get(key) || new Set();
    set.add((it.isbn && it.isbn.length ? `isbn:${it.isbn}` : `title:${it.title}`));
    ownedByStudent.set(key, set);
  }
  const requiredKeys = bundle && Array.isArray(bundle.items)
    ? bundle.items.map(x => (x.isbn && x.isbn.length ? `isbn:${x.isbn}` : `title:${x.title}`))
    : [];
  const coverageRows = studentsInScope.map(s => {
    const key = s.id || s.fullname || s.name;
    const owned = Array.from(ownedByStudent.get(key) || []);
    const missing = requiredKeys.filter(k => !owned.includes(k));
    const section = s.section || (Array.isArray(s.sections) ? s.sections[0] : "");
    return {
      id: key,
      name: s.fullname || s.name || key,
      section,
      ownedCount: owned.length,
      missingCount: missing.length,
      missingList: missing.map(k => k.startsWith("isbn:") ? (k.slice(5)) : (k.slice(6))).join("; "),
    };
  }).sort((a,b) => a.name.localeCompare(b.name));

  function exportCSV(filename, rows) {
    const csv = rows.map(r => r.map(v => `"${String(v ?? "").replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
  }
  function exportDailyCSV() {
    const header = ["Date","OR #","Student","Grade","Qty","Total"];
    const rows = daily.map(s => [s.date, s.orNo || "", s.studentName || "", s.grade || "", Number(s.qty || 0), Number(s.total || 0)]);
    exportCSV(`books_daily_${campus}_${date}.csv`, [header, ...rows]);
  }
  function exportCoverageCSV() {
    const header = ["Student","Section","Owned","Missing","Missing Titles"];
    const rows = coverageRows.map(r => [r.name, r.section || "", r.ownedCount, r.missingCount, r.missingList || ""]);
    exportCSV(`books_coverage_${campus}_${schoolYear}_${gradeFilter || "all"}.csv`, [header, ...rows]);
  }

  return (
    <div className="container-fluid px-0">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <h4 className="mb-0">Books Reports — {campus.toUpperCase()} — {schoolYear}</h4>
          {setCampus && (
            <select className="form-select" value={campus} onChange={e => setCampus(e.target.value)} style={{ width: 140 }}>
              <option value="gapan">gapan</option>
              <option value="bulacan">bulacan</option>
            </select>
          )}
          {setSchoolYear && (
            <select className="form-select" value={schoolYear} onChange={e => setSchoolYear(e.target.value)} style={{ width: 160 }}>
              {(years || []).map(y => <option key={y} value={y}>{y === "all" ? "All Years" : y}</option>)}
            </select>
          )}
        </div>
      </div>
      {USE_LOCAL_FALLBACK && (
        <div className="alert alert-warning">
          Development mode: using local browser storage. In production this will use Firestore.
        </div>
      )}
      <div className="row g-3">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Daily Sales</h5>
                <div className="d-flex gap-2">
                  <input type="date" className="form-control" style={{ width: 180 }} value={date} onChange={e => setDate(e.target.value)} />
                  <button className="btn btn-outline-primary" onClick={exportDailyCSV} disabled={daily.length===0}>Export CSV</button>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-sm table-bordered">
                  <thead className="table-light"><tr><th>Date</th><th>OR #</th><th>Student</th><th>Grade</th><th className="text-end">Qty</th><th className="text-end">Total</th></tr></thead>
                  <tbody>
                    {daily.length === 0 ? (
                      <tr><td colSpan="6" className="text-center text-muted">No sales</td></tr>
                    ) : daily.map(s => (
                      <tr key={s.id}>
                        <td>{s.date}</td>
                        <td>{s.orNo || "-"}</td>
                        <td>{s.studentName || "-"}</td>
                        <td>{s.grade}</td>
                        <td className="text-end">{s.qty}</td>
                        <td className="text-end">₱{Number(s.total || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="fw-bold"><td colSpan="5" className="text-end">Total</td><td className="text-end">₱{dailyTotal.toLocaleString()}</td></tr></tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Monthly Sales</h5>
                <input type="month" className="form-control" style={{ width: 180 }} value={month} onChange={e => setMonth(e.target.value)} />
              </div>
              <div className="table-responsive">
                <table className="table table-sm table-bordered">
                  <thead className="table-light"><tr><th>Month</th><th className="text-end">Transactions</th><th className="text-end">Total</th></tr></thead>
                  <tbody>
                    <tr><td>{month}</td><td className="text-end">{monthly.length}</td><td className="text-end">₱{monthlyTotal.toLocaleString()}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h5 className="mb-2">Publisher Settlement — {schoolYear}</h5>
          <div className="table-responsive">
            <table className="table table-sm table-bordered">
              <thead className="table-light"><tr><th>Publisher</th><th className="text-end">Sold Units</th><th className="text-end">Payable (Cost)</th><th className="text-end">Freight</th><th className="text-end">Discount</th><th className="text-end">Net Payable</th><th></th></tr></thead>
              <tbody>
                {settlementByPublisher.length === 0 ? (
                  <tr><td colSpan="7" className="text-center text-muted">No sold items yet</td></tr>
                ) : settlementByPublisher.map((r, i) => (
                  <tr key={i}>
                    <td>{r.publisher}</td>
                    <td className="text-end">{r.sold}</td>
                    <td className="text-end">₱{Number(r.payable || 0).toLocaleString()}</td>
                    <td className="text-end">
                      <input type="number" className="form-control form-control-sm text-end" style={{ minWidth: 110 }}
                             value={adj[r.publisher]?.freight ?? 0}
                             onChange={e => setAdj(prev => ({ ...prev, [r.publisher]: { ...(prev[r.publisher]||{}), freight: e.target.value } }))} />
                    </td>
                    <td className="text-end">
                      <input type="number" className="form-control form-control-sm text-end" style={{ minWidth: 110 }}
                             value={adj[r.publisher]?.discount ?? 0}
                             onChange={e => setAdj(prev => ({ ...prev, [r.publisher]: { ...(prev[r.publisher]||{}), discount: e.target.value } }))} />
                    </td>
                    <td className="text-end fw-bold">
                      ₱{Number((Number(r.payable||0) + Number(adj[r.publisher]?.freight||0) - Number(adj[r.publisher]?.discount||0)) || 0).toLocaleString()}
                    </td>
                    <td className="text-center">
                      <button className="btn btn-sm btn-outline-primary" onClick={() => saveAdj(r.publisher)}>Save</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card mt-3">
            <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Student Coverage</h5>
            <div className="d-flex gap-2">
              <select className="form-select" value={gradeFilter} onChange={e => setGradeFilter(e.target.value)}>
                <option value="">All Grades</option>
                {gradesAvailable.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
              <input className="form-control" placeholder="Section (optional)" value={sectionFilter} onChange={e => setSectionFilter(e.target.value)} />
              {gradeFilter && coverageRows.length > 0 && (
                <a
                  className="btn btn-outline-primary"
                  target="_blank" rel="noreferrer"
                  href={`/books/print/student-order?campus=${encodeURIComponent(campus)}&sy=${encodeURIComponent(schoolYear)}&grade=${encodeURIComponent(gradeFilter)}&sids=${encodeURIComponent(coverageRows.map(r=>r.id).join(","))}&date=${new Date().toISOString().slice(0,10)}`}
                  title="Print 3-up per page for all filtered students"
                >
                  Print 3‑up (All Filtered)
                </a>
              )}
              <button className="btn btn-outline-secondary" onClick={exportCoverageCSV} disabled={!gradeFilter || coverageRows.length===0}>Export CSV</button>
            </div>
          </div>
          {!gradeFilter ? (
            <div className="text-muted">Select a grade to compare required bundle titles against purchased items.</div>
          ) : !bundle ? (
            <div className="text-muted">No bundle found for the selected grade. Create one in the Bundles tab to enable coverage.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Student</th>
                    <th>Section</th>
                    <th className="text-end">Owned</th>
                    <th className="text-end">Missing</th>
                    <th>Missing Titles</th>
                  </tr>
                </thead>
                <tbody>
                  {coverageRows.length === 0 ? (
                    <tr><td colSpan="5" className="text-center text-muted">No students match filters</td></tr>
                  ) : coverageRows.map(r => (
                    <tr key={r.id}>
                      <td>{r.name}</td>
                      <td>{r.section || "-"}</td>
                      <td className="text-end">{r.ownedCount}</td>
                      <td className="text-end">{r.missingCount}</td>
                      <td>
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{r.missingList || "-"}</span>
                          <a
                            className="btn btn-sm btn-outline-primary ms-2"
                            target="_blank" rel="noreferrer"
                            href={`/books/print/student-order?campus=${encodeURIComponent(campus)}&sy=${encodeURIComponent(schoolYear)}&sid=${encodeURIComponent(r.id)}&grade=${encodeURIComponent(gradeFilter)}&date=${new Date().toISOString().slice(0,10)}`}
                          >
                            Print Sheet
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
